/**
 * Document <head> manager — the only code that writes SEO tags into the DOM.
 *
 * Every element it creates is marked `data-seo="1"` so a later apply() can
 * replace the whole managed set atomically; nothing else in <head> is touched.
 * Idempotent: applying the same meta twice leaves one set of tags.
 *
 * No react-helmet: this is ~100 lines, has no dependency, and the prerender
 * snapshot captures whatever it writes.
 */
import { buildGraph } from './schema';
import {
  SITE_NAME, DEFAULT_OG_IMAGE, DEFAULT_OG_IMAGE_ALT, TWITTER_SITE, ROBOTS_INDEX, ROBOTS_NOINDEX, LOCALE,
} from './siteConfig';
import { APP_DEFAULT_TITLE } from './routes.config';

const MARK = 'data-seo';
let lastAppliedTitle = null;

function head() {
  return typeof document !== 'undefined' ? document.head : null;
}

function removeManaged(h) {
  h.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
}

/** Remove legacy un-managed duplicates of tags we now own (from index.html). */
function removeUnmanagedDuplicates(h) {
  const selectors = [
    'link[rel="canonical"]',
    'meta[name="description"]',
    'meta[name="robots"]',
    'meta[name="keywords"]',
    'meta[property^="og:"]',
    'meta[name^="twitter:"]',
    'script[type="application/ld+json"]',
  ];
  h.querySelectorAll(selectors.join(',')).forEach((el) => {
    if (!el.hasAttribute(MARK)) el.remove();
  });
}

function add(h, tag, attrs, text) {
  const el = document.createElement(tag);
  Object.keys(attrs).forEach((k) => el.setAttribute(k, attrs[k]));
  el.setAttribute(MARK, '1');
  if (text !== undefined) el.textContent = text;
  h.appendChild(el);
  return el;
}

/**
 * Apply resolved route metadata to the document.
 * @param {object} meta  result of resolveRouteMeta()
 */
export function applyHeadMeta(meta) {
  const h = head();
  if (!h || !meta) return;

  removeManaged(h);
  removeUnmanagedDuplicates(h);

  // Title: public pages always; app routes only when they declare one or
  // when the current title is still ours (so a page that sets its own
  // document.title — certificate verification, tenant portals — keeps it).
  const nextTitle = meta.title || (meta.indexable ? SITE_NAME : null);
  if (nextTitle) {
    document.title = nextTitle;
    lastAppliedTitle = nextTitle;
  } else if (document.title === lastAppliedTitle || !document.title) {
    document.title = APP_DEFAULT_TITLE;
    lastAppliedTitle = APP_DEFAULT_TITLE;
  }

  add(h, 'meta', { name: 'robots', content: meta.indexable ? ROBOTS_INDEX : ROBOTS_NOINDEX });

  if (meta.canonical) add(h, 'link', { rel: 'canonical', href: meta.canonical });

  if (meta.description) add(h, 'meta', { name: 'description', content: meta.description });

  if (meta.indexable && meta.canonical) {
    const ogTitle = meta.ogTitle || meta.title;
    const ogDesc = meta.ogDescription || meta.description;
    const ogImage = meta.ogImage || DEFAULT_OG_IMAGE;
    add(h, 'meta', { property: 'og:site_name', content: SITE_NAME });
    add(h, 'meta', { property: 'og:type', content: meta.ogType || 'website' });
    add(h, 'meta', { property: 'og:title', content: ogTitle });
    add(h, 'meta', { property: 'og:description', content: ogDesc });
    add(h, 'meta', { property: 'og:url', content: meta.canonical });
    add(h, 'meta', { property: 'og:image', content: ogImage });
    add(h, 'meta', { property: 'og:image:alt', content: meta.ogImageAlt || DEFAULT_OG_IMAGE_ALT });
    add(h, 'meta', { property: 'og:locale', content: LOCALE });
    add(h, 'meta', { name: 'twitter:card', content: 'summary_large_image' });
    add(h, 'meta', { name: 'twitter:site', content: TWITTER_SITE });
    add(h, 'meta', { name: 'twitter:title', content: ogTitle });
    add(h, 'meta', { name: 'twitter:description', content: ogDesc });
    add(h, 'meta', { name: 'twitter:image', content: ogImage });

    const graph = buildGraph(meta);
    if (graph) add(h, 'script', { type: 'application/ld+json' }, JSON.stringify(graph));
  }

  // Signal for the prerenderer and for tests.
  document.documentElement.setAttribute('data-seo-path', meta.pathname || '');
  document.documentElement.setAttribute('data-seo-ready', '1');
}

/** Snapshot of the managed state, for hooks that want to restore on unmount. */
export function snapshotHead() {
  const h = head();
  if (!h) return null;
  return {
    title: document.title,
    nodes: Array.from(h.querySelectorAll(`[${MARK}]`)).map((el) => el.cloneNode(true)),
  };
}

export function restoreHead(snapshot) {
  const h = head();
  if (!h || !snapshot) return;
  removeManaged(h);
  snapshot.nodes.forEach((n) => h.appendChild(n));
  document.title = snapshot.title;
  lastAppliedTitle = snapshot.title;
}

/** Test helper. */
export function __resetHeadManagerForTests() {
  lastAppliedTitle = null;
  const h = head();
  if (h) removeManaged(h);
}
